'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useGetAdBoardsQuery, type IAdBoard } from '@/api/ad-boards/queries/get-ad-boards-query'
import {
    useAddAdToBoardMutation,
    useCreateAdBoardMutation,
    useRemoveAdFromBoardMutation,
} from '@/api/ad-boards/mutations/ad-board-mutations'
import { useBrandRoute } from '@/setup/routing/use-brand-router'
import { Route } from '@/setup/routing/routing'
import { cn } from '@/utils/styles/cn'
import { IClassNameProps } from '@/utils/typings/class-name-props'
import Link from 'next/link'
import { ArrowUpRight, Check, LayoutGrid, Loader2, Plus, Search } from 'lucide-react'

/** Above this many boards the list is worth searching rather than scanning. */
const SEARCH_THRESHOLD = 8

interface IAddToBoardButtonProps extends IClassNameProps {
    adUuid: string
    /** Replaces the default icon-button trigger, e.g. with a card-overlay glass button. */
    trigger?: (state: { open: boolean }) => React.ReactNode
    /**
     * Fires when the ad lands on a board (picked or newly created). The split save button records
     * the board as the last-used one-click target.
     */
    onAdded?: (board: { uuid: string; name: string }) => void
    /** Fires when the ad is removed from a board — the split save button drops its saved state. */
    onRemoved?: (board: { uuid: string; name: string }) => void
}

/**
 * Puts an ad on a board without leaving the research grid.
 *
 * The popover deliberately stays open across actions: curating means filing one ad into several
 * boards, and a menu that closes on each pick turns that into repeated re-opening. Each row shows
 * whether it already holds this ad, so the same control adds and removes.
 */
export const AddToBoardButton = ({ adUuid, className, trigger, onAdded, onRemoved }: IAddToBoardButtonProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [isNaming, setIsNaming] = useState(false)
    const [newBoardName, setNewBoardName] = useState('')
    const [lastCreated, setLastCreated] = useState<{ uuid: string; name: string } | null>(null)

    // Only fetched while open: the contains-state is per ad, so this would otherwise be one
    // request per card in the grid.
    const { data: boards, isLoading } = useGetAdBoardsQuery({ forAdUuid: isOpen ? adUuid : undefined })
    const addToBoard = useAddAdToBoardMutation()
    const removeFromBoard = useRemoveAdFromBoardMutation()
    const createBoard = useCreateAdBoardMutation()

    const visibleBoards = (boards ?? []).filter((board) =>
        search.trim() ? board.name.toLowerCase().includes(search.trim().toLowerCase()) : true
    )

    const handleToggle = async (board: IAdBoard) => {
        if (board.containsAd) {
            await removeFromBoard.mutateAsync({ boardUuid: board.uuid, adUuid })
            onRemoved?.({ uuid: board.uuid, name: board.name })
        } else {
            await addToBoard.mutateAsync({ boardUuid: board.uuid, adUuid })
            onAdded?.({ uuid: board.uuid, name: board.name })
        }
    }

    const handleCreate = async () => {
        const name = newBoardName.trim()
        if (!name) return

        const board = await createBoard.mutateAsync({ name }).catch(() => null)
        if (!board) return

        await addToBoard.mutateAsync({ boardUuid: board.uuid, adUuid })
        onAdded?.({ uuid: board.uuid, name: board.name })

        setLastCreated({ uuid: board.uuid, name: board.name })
        setNewBoardName('')
        setIsNaming(false)
    }

    const isPending = addToBoard.isPending || removeFromBoard.isPending || createBoard.isPending

    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open)
                if (!open) {
                    setSearch('')
                    setIsNaming(false)
                    setNewBoardName('')
                    setLastCreated(null)
                }
            }}
        >
            {trigger ? (
                <PopoverTrigger asChild>{trigger({ open: isOpen })}</PopoverTrigger>
            ) : (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className={cn('h-7 w-7 p-0', className)}
                                aria-label="Add to board"
                            >
                                <LayoutGrid className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Add to board</TooltipContent>
                </Tooltip>
            )}

            <PopoverContent align="end" className="w-[260px] p-0">
                <div className="flex flex-col">
                    {(boards?.length ?? 0) > SEARCH_THRESHOLD ? (
                        <div className="flex items-center gap-2 border-b border-black/5 px-3 py-2">
                            <Search className="h-3.5 w-3.5 text-muted-foreground" />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search boards"
                                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                            />
                        </div>
                    ) : null}

                    <div className="max-h-[240px] overflow-y-auto py-1">
                        {isLoading ? (
                            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Loading boards
                            </div>
                        ) : visibleBoards.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-muted-foreground">
                                {search.trim() ? 'No board matches.' : 'No boards yet.'}
                            </p>
                        ) : (
                            visibleBoards.map((board) => (
                                <button
                                    key={board.uuid}
                                    type="button"
                                    disabled={isPending}
                                    onClick={() => handleToggle(board)}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/[0.04] disabled:opacity-60"
                                >
                                    <Check
                                        className={cn(
                                            'h-3.5 w-3.5 shrink-0',
                                            board.containsAd ? 'opacity-100' : 'opacity-0'
                                        )}
                                    />
                                    <span className="truncate">{board.name}</span>
                                </button>
                            ))
                        )}
                    </div>

                    <div className="border-t border-black/5 p-1">
                        {isNaming ? (
                            <div className="flex items-center gap-1 p-1">
                                <Input
                                    autoFocus
                                    value={newBoardName}
                                    onChange={(event) => setNewBoardName(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') handleCreate()
                                        if (event.key === 'Escape') setIsNaming(false)
                                    }}
                                    placeholder="Board name"
                                    className="h-8 text-sm"
                                />
                                <Button
                                    size="sm"
                                    variant="ds2-secondary"
                                    isLoading={createBoard.isPending}
                                    disabled={!newBoardName.trim()}
                                    onClick={handleCreate}
                                >
                                    Add
                                </Button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsNaming(true)}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-black/[0.04]"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                New board
                            </button>
                        )}
                    </div>

                    {lastCreated ? (
                        <BoardCreatedConfirmation uuid={lastCreated.uuid} name={lastCreated.name} />
                    ) : null}
                </div>
            </PopoverContent>
        </Popover>
    )
}

const BoardCreatedConfirmation = ({ uuid, name }: { uuid: string; name: string }) => {
    const { getRoute } = useBrandRoute()

    return (
        <div className="flex items-center justify-between gap-2 border-t border-black/5 px-3 py-2">
            <span className="truncate text-xs text-muted-foreground">Added to {name}</span>
            <Link
                href={getRoute(Route.BOARD_DETAIL).getUrl({ board_uuid: uuid })}
                className="flex shrink-0 items-center gap-0.5 text-xs font-medium hover:underline"
            >
                Open
                <ArrowUpRight className="h-3 w-3" />
            </Link>
        </div>
    )
}
